import {
  Button,
  column,
  Css,
  emptyCell,
  FullBleed,
  GridColumn,
  GridDataRow,
  GridTable,
  HbLoadingSpinner,
  IconButton,
  RowStyles,
  ScrollableContent,
  SelectField,
  simpleHeader,
  Tag,
  useModal,
  useSnackbar,
  useToast,
} from "@homebound/beam";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTrackedJobs } from "src/contexts/TrackedJobsContext";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { PageHeader } from "src/routes/layout/PageHeader";
import { useSaveMavMutation } from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { createMaterialCatalogUrl, createMaterialDetailsUrl } from "src/RouteUrls";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";
import { formatTakeoffUsageSummary } from "../components/takeoffs-usage-modal/formatTakeoffUsageSummary";
import { TakeoffsUsageModal, takeoffsUsageModalOptions } from "../components/takeoffs-usage-modal/TakeoffsUsageModal";
import {
  buildMavSelectOptions,
  isRemoveAttributeOption,
  mavIdsForMerge,
  MavOptionSource,
  MavSelectOption,
  REMOVE_ATTRIBUTE_OPTION_ID,
} from "./buildMavSelectOptions";
import {
  buildMergeMaterialVariantsInput,
  createDefaultFieldSelections,
  MergeFieldSelections,
  reconcileFieldSelections,
  uniqueListings,
} from "./buildMergeMaterialVariantsInput";
import { FeaturedImagePickerModal } from "./FeaturedImagePickerModal";
import {
  MergeMaterialVariants_MaterialVariantFragment,
  useMergeMaterialVariants_PreviewDisplayNameQuery,
  useMergeMaterialVariants_SelectionQuery,
  useMergeMaterialVariantsMutation,
} from "./MergeMaterialVariants.generated";
import { SelectMaterialsModal } from "./SelectMaterialsModal";

type MergeMaterialVariantsPageProps = {
  /** Optional seed for Storybook / tests so the comparison table renders without manual selection. */
  initialSelectedIds?: string[];
};

export function MergeMaterialVariantsPage(props: MergeMaterialVariantsPageProps) {
  const { initialSelectedIds = [] } = props;
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { addJob } = useTrackedJobs();
  const { triggerNotice } = useSnackbar();
  const { showToast } = useToast();
  const [merge] = useMergeMaterialVariantsMutation();
  const [saveMav] = useSaveMavMutation();

  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const [fieldSelections, setFieldSelections] = useState<MergeFieldSelections | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Newly created MAVs not yet present on dimension.values from the selection query. */
  const [extraMavsByDimensionId, setExtraMavsByDimensionId] = useState<Record<string, MavOptionSource[]>>({});

  const selectionQuery = useMergeMaterialVariants_SelectionQuery({
    variables: { filter: { id: selectedIds, includeDeleted: true } },
    skip: selectedIds.isEmpty,
  });

  // Filter by selectedIds so removing a row updates the table immediately (before Apollo refetches).
  const variants = useMemo(() => {
    const entities = selectionQuery.data?.materialVariants.entities ?? [];
    return selectedIds.flatMap((id) => entities.find((v) => v.id === id) ?? []);
  }, [selectionQuery.data?.materialVariants.entities, selectedIds]);

  const selectionComplete =
    selectedIds.nonEmpty &&
    variants.length === selectedIds.length &&
    selectedIds.every((id) => variants.some((v) => v.id === id));

  // Track the selected id *set* so Apollo rebroadcasts of the same entities don't rebuild defaults.
  const selectedIdsKey = selectedIds.join("\0");
  const appliedSelectedIdsKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (selectedIds.isEmpty) {
      setFieldSelections(undefined);
      appliedSelectedIdsKeyRef.current = undefined;
      return;
    }
    if (!selectionComplete) return;

    const idsChanged = appliedSelectedIdsKeyRef.current !== selectedIdsKey;
    appliedSelectedIdsKeyRef.current = selectedIdsKey;

    setFieldSelections((prev) => {
      if (!prev) return createDefaultFieldSelections(variants);
      // Same selection set (e.g. refetch) — keep custom picks as-is.
      if (!idsChanged) return prev;
      // Add/remove — keep valid picks, only replace values that no longer exist among remaining MVs.
      return reconcileFieldSelections(prev, variants);
    });
  }, [selectedIds, selectedIdsKey, variants, selectionComplete]);

  const lockedItemId = variants.first?.listing.item.id;
  const dimensions = useMemo(
    () => (variants.first?.listing.item.materialAttributeDimensions ?? []).sortBy((d) => d.sortOrder),
    [variants],
  );

  const previewDisplayNameInput = useMemo(() => {
    if (!fieldSelections || !selectionComplete) return undefined;
    return {
      listingId: fieldSelections.listingId,
      modelNumber: fieldSelections.modelNumber,
      mavIds: mavIdsForMerge(fieldSelections.mavByDimensionId),
    };
  }, [fieldSelections, selectionComplete]);

  const previewDisplayNameQuery = useMergeMaterialVariants_PreviewDisplayNameQuery({
    variables: { input: previewDisplayNameInput! },
    skip: !previewDisplayNameInput,
  });
  const mergedDisplayName = previewDisplayNameQuery.data?.previewMaterialVariantDisplayName;

  const mergeDisabledReason = isSubmitting
    ? "Merge in progress"
    : selectedIds.length < 2
      ? "Select at least 2 materials to merge"
      : !selectionComplete || !fieldSelections
        ? "Loading material details…"
        : undefined;

  function openSelectModal() {
    openModal({
      content: (
        <SelectMaterialsModal selectedIds={selectedIds} lockedItemId={lockedItemId} onConfirm={setSelectedIds} />
      ),
      size: "xxl",
    });
  }

  const openImagePicker = useCallback(() => {
    if (!fieldSelections) return;
    openModal({
      content: (
        <FeaturedImagePickerModal
          variants={variants}
          selectedImageId={fieldSelections.featuredImageId}
          onSelect={(featuredImageId) => setFieldSelections((prev) => (prev ? { ...prev, featuredImageId } : prev))}
        />
      ),
      size: "md",
    });
  }, [fieldSelections, openModal, variants]);

  const saveNewMav = useCallback(
    async (dimensionId: string, textValue: string) => {
      const { data } = await saveMav({ variables: { input: { dimensionId, textValue } } });
      const created = data?.saveMaterialAttributeValue.materialAttributeValue;
      if (!created) {
        showToast({ type: "error", message: "Failed to create attribute value." });
        return;
      }
      setExtraMavsByDimensionId((prev) => ({
        ...prev,
        [dimensionId]: [...(prev[dimensionId] ?? []), { id: created.id, textValue: created.textValue }].uniqueBy(
          (m) => m.id,
        ),
      }));
      setFieldSelections((prev) =>
        prev ? { ...prev, mavByDimensionId: { ...prev.mavByDimensionId, [dimensionId]: created.id } } : prev,
      );
    },
    [saveMav, showToast],
  );

  function confirmAndMerge() {
    if (!fieldSelections || !selectionComplete || variants.length < 2) return;
    const displayName = mergedDisplayName ?? "merged material";
    const deletedCount = variants.length - 1;
    const uniqueUsages = variants.flatMap((mv) => mv.takeoffUsages).uniqueBy((u) => u.id);
    const takeoffs = uniqueUsages.count((u) => u.__typename === "PlanPackage");
    const designPackages = uniqueUsages.count((u) => u.__typename === "DesignPackage");
    const productOfferings = uniqueUsages.count((u) => u.__typename === "ProductOffering");
    const usageParts = [
      takeoffs > 0 ? `${takeoffs} Takeoff${takeoffs === 1 ? "" : "s"}` : undefined,
      designPackages > 0 ? `${designPackages} Design Package${designPackages === 1 ? "" : "s"}` : undefined,
      productOfferings > 0 ? `${productOfferings} Product Offering${productOfferings === 1 ? "" : "s"}` : undefined,
    ].compact();
    const usageSentence = usageParts.isEmpty
      ? "These are not currently referenced in any takeoffs, design packages, or product offerings."
      : `The references in ${joinAnd(usageParts)} will be updated to point to the merged material.`;
    openModal({
      content: (
        <MergeConfirmModal
          deletedCount={deletedCount}
          displayName={displayName}
          onMerge={async () => {
            const input = buildMergeMaterialVariantsInput(variants, fieldSelections, "MANUAL");
            const { data } = await merge({ variables: { input } });
            const jobId = data?.mergeMaterialVariants.trackedJob.id;
            if (!jobId) {
              showToast({ type: "error", message: "Failed to start merge job." });
              return;
            }
            const materialCount = variants.length;
            addJob(jobId, "Merging material variants…", {
              silent: true,
              onComplete: () => {
                triggerNotice({ message: `${materialCount} materials have been merged.` });
              },
              onFailed: (message) => {
                showToast({ type: "error", message: message ?? "Failed to merge materials." });
              },
            });
            navigate(createMaterialCatalogUrl());
          }}
          setPageSubmitting={setIsSubmitting}
          usageSentence={usageSentence}
        />
      ),
    });
  }

  const openTakeoffsUsage = useCallback(
    (materialVariantId: string) => {
      openModal({
        content: <TakeoffsUsageModal materialVariantId={materialVariantId} />,
        ...takeoffsUsageModalOptions,
      });
    },
    [openModal],
  );

  const rows = useMemo(() => createRows(variants, fieldSelections), [variants, fieldSelections]);
  const columns = useMemo(
    () =>
      createColumns(
        variants,
        dimensions,
        fieldSelections,
        setFieldSelections,
        openImagePicker,
        openTakeoffsUsage,
        setSelectedIds,
        saveNewMav,
        extraMavsByDimensionId,
        mergedDisplayName,
      ),
    [
      variants,
      dimensions,
      fieldSelections,
      openImagePicker,
      openTakeoffsUsage,
      saveNewMav,
      extraMavsByDimensionId,
      mergedDisplayName,
    ],
  );

  // Remount when dimension columns appear/change so Beam recalculates columnSizes from scratch.
  // Otherwise the columns can get resized incorrectly when MVs are added/removed due to the column count changing
  const tableLayoutKey = dimensions.map((d) => d.id).join(",") || "no-dimensions";

  return (
    <div css={Css.w100.mw0.h100.$}>
      <PageHeader
        title="Merge Duplicate Materials"
        breadcrumb={[{ href: createMaterialCatalogUrl(), label: "Material Catalog" }]}
        right={
          <div css={Css.df.gap2.$}>
            <Button label="Cancel" variant="quaternary" onClick={createMaterialCatalogUrl()} />
            <Button label="Merge" disabled={mergeDisabledReason} onClick={confirmAndMerge} />
          </div>
        }
        xss={Css.m0.py3.$}
      />
      <PageHeader
        left={
          <div css={Css.sm.gray900.$}>Select materials below, then choose which data to keep for each attribute.</div>
        }
        right={
          <div css={Css.df.gap2.$}>
            <Button icon="plus" label="Add" variant="secondary" onClick={openSelectModal} />
          </div>
        }
        xss={Css.m0.py4.$}
      />
      {/* Headers stay outside ScrollableContent so horizontal table scroll doesn't drag them. */}
      <ScrollableContent>
        <FullBleed omitPadding>
          <div css={Css.mw0.$}>
            {selectionQuery.loading && !selectionQuery.data && selectedIds.nonEmpty ? (
              <HbLoadingSpinner />
            ) : (
              <GridTable
                key={tableLayoutKey}
                columns={columns}
                rows={rows}
                stickyHeader
                disableColumnResizing
                rowStyles={rowStyles}
                emptyState={
                  selectionQuery.error
                    ? {
                        title: "Couldn't load materials",
                        description: "Try again or reselect materials to merge.",
                        actions: <Button label="Select" variant="secondary" onClick={openSelectModal} />,
                      }
                    : {
                        title: "No materials selected yet",
                        description: "Search for and select materials to see and compare their attributes.",
                        actions: <Button label="Select" variant="secondary" onClick={openSelectModal} />,
                      }
                }
              />
            )}
          </div>
        </FullBleed>
      </ScrollableContent>
    </div>
  );
}

type Dimension = { id: string; name: string; sortOrder: number; values: Array<{ id: string; textValue: string }> };

type MergeConfirmModalProps = {
  deletedCount: number;
  displayName: string;
  onMerge: () => Promise<void>;
  setPageSubmitting: (submitting: boolean) => void;
  usageSentence: string;
};

/** Owns submitting state so ConfirmationModal's disabled prop updates on re-render - protects against double clicks adding multiple jobs. */
function MergeConfirmModal(props: MergeConfirmModalProps) {
  const { deletedCount, displayName, onMerge, setPageSubmitting, usageSentence } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inFlightRef = useRef(false);
  const { showToast } = useToast();

  return (
    <ConfirmationModal
      title="Merge These Materials"
      label="Merge"
      disabled={isSubmitting}
      confirmationMessage={
        <div css={Css.sm.gray700.$}>
          <p>
            This can’t be undone. {deletedCount} duplicate material{deletedCount === 1 ? "" : "s"} will be deleted and
            merged into <span css={Css.smSb.gray900.$}>{displayName}</span>.
          </p>
          <p css={Css.mt2.$}>{usageSentence}</p>
        </div>
      }
      onConfirmAction={async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setIsSubmitting(true);
        setPageSubmitting(true);
        try {
          await onMerge();
        } catch (e) {
          showToast({ type: "error", message: e instanceof Error ? e.message : "Failed to merge materials." });
        } finally {
          inFlightRef.current = false;
          setIsSubmitting(false);
          setPageSubmitting(false);
        }
      }}
    />
  );
}

type HeaderRow = { kind: "header" };
type MergedRow = { kind: "merged"; data?: undefined };
type SourceRow = { kind: "source"; id: string; data: MergeMaterialVariants_MaterialVariantFragment };
type Row = HeaderRow | MergedRow | SourceRow;

const rowStyles: RowStyles<Row> = { header: {}, merged: { cellCss: { backgroundColor: "bgBlue50" } }, source: {} };

function createRows(
  variants: MergeMaterialVariants_MaterialVariantFragment[],
  fieldSelections: MergeFieldSelections | undefined,
): GridDataRow<Row>[] {
  if (variants.isEmpty || !fieldSelections) return [simpleHeader];
  return [
    simpleHeader,
    // Pin without a pin icon so the merged preview stays sticky under the header while scrolling.
    { kind: "merged", id: "merged", data: undefined, initPinned: true },
    ...variants.map((data) => ({ kind: "source" as const, id: data.id, data })),
  ];
}

function createColumns(
  variants: MergeMaterialVariants_MaterialVariantFragment[],
  dimensions: Dimension[],
  fieldSelections: MergeFieldSelections | undefined,
  setFieldSelections: (next: MergeFieldSelections) => void,
  openImagePicker: () => void,
  openTakeoffsUsage: (materialVariantId: string) => void,
  setSelectedIds: (ids: string[] | ((prev: string[]) => string[])) => void,
  saveNewMav: (dimensionId: string, textValue: string) => Promise<void>,
  extraMavsByDimensionId: Record<string, MavOptionSource[]>,
  mergedDisplayName: string | undefined,
): GridColumn<Row>[] {
  const listings = uniqueListings(variants);
  const featuredImage =
    variants.flatMap((mv) => mv.images).find((img) => img.id === fieldSelections?.featuredImageId) ??
    variants.map((mv) => mv.featuredImage).compact().first;

  const brandOptions = variants
    .map((mv) => mv.listing.brand)
    .compact()
    .uniqueBy((brand) => brand.id)
    .map((brand) => ({ id: brand.id, name: brand.name }));
  const modelOptions = variants
    .map((mv) => ({ id: mv.modelNumber ?? "", name: mv.modelNumber || "—" }))
    .uniqueBy((o) => o.id);
  const urlOptions = variants
    .map((mv) => ({ id: mv.manufacturerUrl ?? "", name: mv.manufacturerUrl || "—" }))
    .uniqueBy((o) => o.id);

  function update(partial: Partial<MergeFieldSelections>) {
    if (!fieldSelections) return;
    setFieldSelections({ ...fieldSelections, ...partial });
  }

  return [
    column<Row>({
      id: "materials",
      header: "Materials",
      merged: () => (
        <div css={Css.df.fdc.$}>
          <MergedMaterialLabel />
          {/* Match the image height so the name centers beside the swatch. */}
          <div css={Css.df.aic.hPx(120).$}>
            <div css={Css.sm.gray900.$} data-testid="mergedMaterialName">
              {mergedDisplayName ?? "—"}
            </div>
          </div>
        </div>
      ),
      source: ({ displayName }) => displayName,
      w: "200px",
    }),
    column<Row>({
      id: "image",
      header: "Image",
      merged: () => (
        <div css={Css.df.fdc.$}>
          {/* Same label band height as Materials so the image sits below the divider. */}
          <MergedMaterialLabel invisible />
          <div css={Css.relative.sqPx(120).fs0.$} data-testid="mergedImage">
            <img
              css={Css.w100.h100.br4.$}
              src={featuredImage?.asset?.previewUrl || featuredImage?.asset?.downloadUrl || PRODUCT_FALLBACK_IMG_URL}
              alt=""
            />
            {variants.some((mv) => mv.images.nonEmpty) && (
              <div css={Css.absolute.top1.right1.bgWhite.br4.$}>
                <IconButton icon="pencil" onClick={openImagePicker} />
              </div>
            )}
          </div>
        </div>
      ),
      source: ({ featuredImage: img }) => (
        <img
          css={Css.sqPx(120).br4.$}
          src={img?.asset?.previewUrl || img?.asset?.downloadUrl || PRODUCT_FALLBACK_IMG_URL}
          alt=""
        />
      ),
      w: "145px",
    }),
    column<Row>({
      id: "sku",
      header: "SKU / Product Code",
      merged: () =>
        fieldSelections
          ? mergedBelowLabel(
              <SelectField
                label="SKU"
                compact
                options={modelOptions}
                value={fieldSelections.modelNumber ?? ""}
                onSelect={(modelNumber) => update({ modelNumber: modelNumber || null })}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
              />,
            )
          : emptyCell,
      source: ({ modelNumber }) => modelNumber || "—",
      w: "180px",
    }),
    column<Row>({
      id: "listing",
      header: "Listing",
      merged: () =>
        fieldSelections
          ? mergedBelowLabel(
              <SelectField
                label="Listing"
                compact
                options={listings}
                value={fieldSelections.listingId}
                onSelect={(listingId) => listingId && update({ listingId })}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
              />,
            )
          : emptyCell,
      source: (mv) => (
        <div css={Css.df.fdc.gap1.$}>
          <div>{mv.listing.name || mv.displayName}</div>
          <Link
            css={Css.blue700.$}
            to={createMaterialDetailsUrl(mv.listing.id, { mv: mv.id })}
            target="_blank"
            rel="noreferrer"
          >
            Listing Page
          </Link>
        </div>
      ),
      w: "200px",
    }),
    column<Row>({
      id: "brand",
      header: "Brand / Manufacturer",
      merged: () =>
        fieldSelections
          ? mergedBelowLabel(
              <SelectField
                label="Brand"
                compact
                options={brandOptions}
                value={fieldSelections.brandId}
                onSelect={(brandId) => update({ brandId: brandId ?? undefined })}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
              />,
            )
          : emptyCell,
      source: ({ listing }) => listing.brand?.name || "—",
      w: "160px",
    }),
    column<Row>({
      id: "website",
      header: "Website",
      merged: () =>
        fieldSelections
          ? mergedBelowLabel(
              <SelectField
                label="Website"
                compact
                options={urlOptions}
                value={fieldSelections.manufacturerUrl ?? ""}
                onSelect={(manufacturerUrl) => update({ manufacturerUrl: manufacturerUrl || null })}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
              />,
            )
          : emptyCell,
      source: ({ manufacturerUrl }) =>
        manufacturerUrl ? (
          <a
            css={Css.blue700.db.w100.wbba.$}
            href={manufacturerUrl}
            target="_blank"
            rel="noreferrer"
            title={manufacturerUrl}
          >
            {manufacturerUrl}
          </a>
        ) : (
          "—"
        ),
      w: "220px",
    }),
    ...dimensions.map((dimension) => {
      const selectedMvMavs = variants
        .map((mv) => mv.materialAttributeValues.find((mav) => mav.dimension.id === dimension.id))
        .compact()
        .map((mav) => ({ id: mav.id, textValue: mav.textValue }));
      const options = buildMavSelectOptions({
        selectedMvMavs,
        dimensionValues: dimension.values,
        extraMavs: extraMavsByDimensionId[dimension.id],
      });
      const selectedMavId = fieldSelections?.mavByDimensionId[dimension.id];
      const isRemoved = isRemoveAttributeOption(selectedMavId);
      const removedCss = Css.if(isRemoved).bgGray100.$;

      return column<Row>({
        id: `mav-${dimension.id}`,
        header: () => ({ content: dimension.name, css: removedCss }),
        merged: () =>
          fieldSelections
            ? {
                content: mergedBelowLabel(
                  <SelectField
                    label={dimension.name}
                    compact
                    autoSort={false}
                    options={options}
                    value={selectedMavId}
                    onSelect={(mavId) =>
                      update({
                        mavByDimensionId: {
                          ...fieldSelections.mavByDimensionId,
                          [dimension.id]: mavId ?? REMOVE_ATTRIBUTE_OPTION_ID,
                        },
                      })
                    }
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    getOptionMenuLabel={(o) => mavOptionMenuLabel(o)}
                    onAddNew={(textValue) => saveNewMav(dimension.id, textValue)}
                  />,
                ),
                css: removedCss,
              }
            : emptyCell,
        source: (mv) => ({
          content: mv.materialAttributeValues.find((mav) => mav.dimension.id === dimension.id)?.textValue || "—",
          css: removedCss,
        }),
        w: "160px",
        // Without mw, GridTable shrinks these attribute columns under horizontal pressure and it was sometimes causing issues with the layout due to the column count changing when MVs were added
        mw: "160px",
      });
    }),
    column<Row>({
      id: "usage",
      header: "Usage",
      merged: () => mergedBelowLabel(null),
      source: (mv) => {
        const summary = formatTakeoffUsageSummary(mv.takeoffUsages);
        if (!summary) return "—";
        return (
          <button
            type="button"
            data-testid="takeoffsUsageLink"
            css={Css.blue700.tal.bn.bgTransparent.p0.cursorPointer.tdu.db.w100.mw0.wbba.$}
            title={summary}
            onClick={() => openTakeoffsUsage(mv.id)}
          >
            {summary}
          </button>
        );
      },
      w: "200px",
    }),
    column<Row>({
      id: "actions",
      header: "",
      merged: () => mergedBelowLabel(null),
      source: (mv) => (
        <IconButton icon="trash" onClick={() => setSelectedIds((ids) => ids.filter((id) => id !== mv.id))} />
      ),
      w: "80px",
    }),
  ];
}

/**
 * Places merged-row cell content in the same 120px band as the image, under a spacer matching
 * the Materials-column "Merged Material" label height so selects line up with the swatch.
 */
function mergedBelowLabel(content: ReactNode) {
  return (
    <div css={Css.df.fdc.$}>
      <MergedMaterialLabel invisible />
      <div css={Css.df.aic.hPx(120).$}>{content}</div>
    </div>
  );
}

/**
 * "Merged Material" label + divider in the Materials column only.
 * Other merged cells pass `invisible` for an empty spacer of the same height (no border/text).
 */
function MergedMaterialLabel(props: { invisible?: boolean }) {
  const { invisible = false } = props;
  if (invisible) {
    return <div css={Css.hPx(30).fs0.$} aria-hidden />;
  }
  return (
    <div css={Css.df.aic.hPx(30).bb.bcGray200.$}>
      <div css={Css.smSb.gray900.pb1.$}>Merged Material</div>
    </div>
  );
}

function mavOptionMenuLabel(option: MavSelectOption) {
  if (option.id === REMOVE_ATTRIBUTE_OPTION_ID) return "Remove Attribute";
  if (!option.isFromSelectedMv) return option.name;
  return (
    <div css={Css.df.aic.gap1.jcsb.w100.$}>
      <span>{option.name}</span>
      <Tag text="Selected" type="neutral" />
    </div>
  );
}

function joinAnd(parts: string[]): string {
  if (parts.length === 1) return parts[0]!;
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts.last}`;
}
